import UIList from 'UIComponents/list/UIList';
import { useUserOwnsPlaylist } from '../../savedItems';
import { usePlaylistSearchManagerOpenState } from '..';
import { useLesson } from '../../lessons';
import { useTrack } from '../../tracks';
import styled, { theme, useMediaQuery } from 'academy-common-lib/utils/Styling';
import UIFlex from 'UIComponents/layout/UIFlex';
import { useAssetStatus } from '../../userProgress';
import H4 from 'UIComponents/elements/headings/H4';
import H5 from 'UIComponents/elements/headings/H5';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { PlaylistDragProvider, PlaylistItem } from './PlaylistItem';
import { PlaylistItemManager, usePlaylistItemManager } from './PlaylistItemManager';
import { CancelPlaylistSearchButton, PlaylistItemSearch, PlaylistItemSearchManager, PlaylistItemSearchProvider, SavePlaylistSearchButton, usePlaylistItemSearch } from './PlaylistItemSearchManager';
import { PlaylistItemSearchResults } from './PlaylistItemSearchResults';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
// @ts-expect-error no types
import TracksTopFiltersContainer from '../../../containers/TracksPage/TracksTopFiltersContainer';
import UIStickyFooter from 'UIComponents/panel/UIStickyFooter';
import { useRef } from 'react';
import { PlaylistMobileFilterButton } from './PlaylistMobileFilter';
import DividedList from '../../../components/DividedList';
import { useShort } from '../../shortContent';
import { HasGate } from '../../accounts';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const Footer = styled(UIStickyFooter).withConfig({
  displayName: "PlaylistItems__Footer",
  componentId: "pvzpfd-0"
})(["background-color:", ";padding:16px 8px 20px;"], theme.colors.GYPSUM);
const PlaylistItemsList = styled(UIList).withConfig({
  displayName: "PlaylistItems__PlaylistItemsList",
  componentId: "pvzpfd-1"
})([".uiListItem:last-child .playlist-progress-indicator{border-color:transparent;}"]);
const LessonPlaylistItem = ({
  playlistItem,
  playlistId,
  position
}) => {
  const {
    lesson
  } = useLesson(playlistItem.itemId, playlistItem.language);
  const {
    status
  } = useAssetStatus({
    id: playlistItem.itemId,
    type: 'LESSON'
  });
  if (!lesson) return null;
  return /*#__PURE__*/_jsx(PlaylistItem, {
    "data-test-id": "playlist-item",
    "data-test-item-id": playlistItem.itemId,
    "data-test-item-type": "LESSON",
    playlistId: playlistId,
    playlistItem: playlistItem,
    position: position,
    status: status === 'EXPIRING_SOON' ? 'COMPLETED' : status
  });
};
const TrackPlaylistItem = ({
  playlistItem,
  playlistId,
  position
}) => {
  const {
    track
  } = useTrack(playlistItem.itemId, playlistItem.language);
  const {
    status
  } = useAssetStatus({
    id: playlistItem.itemId,
    type: 'TRACK'
  });
  if (!track) return null;
  return /*#__PURE__*/_jsx(PlaylistItem, {
    "data-test-id": "playlist-item",
    "data-test-item-id": playlistItem.itemId,
    "data-test-item-type": "TRACK",
    playlistId: playlistId,
    playlistItem: playlistItem,
    position: position,
    status: status === 'EXPIRING_SOON' ? 'COMPLETED' : status
  });
};
const ShortPlaylistItem = ({
  playlistItem,
  playlistId,
  position
}) => {
  const {
    short
  } = useShort(playlistItem.itemId, playlistItem.language);
  const {
    status
  } = useAssetStatus({
    id: playlistItem.itemId,
    type: 'SHORT_FORM_CONTENT'
  });
  if (!short) return null;
  return /*#__PURE__*/_jsx(HasGate, {
    gate: "Academy:ShortFormContent",
    children: /*#__PURE__*/_jsx(PlaylistItem, {
      "data-test-id": "playlist-item",
      "data-test-item-id": playlistItem.itemId,
      "data-test-item-type": "SHORT",
      playlistId: playlistId,
      playlistItem: playlistItem,
      position: position,
      status: status === 'EXPIRING_SOON' ? 'COMPLETED' : status
    })
  });
};
const PlaylistItemCount = () => {
  const {
    numberOfSelectedItems: selectedExistingPlaylistItems
  } = usePlaylistItemManager();
  const {
    numberOfSelectedItems: selectedNewPlaylistItems
  } = usePlaylistItemSearch();
  const count = selectedNewPlaylistItems > 0 ? selectedNewPlaylistItems : selectedExistingPlaylistItems;
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.savedItems.playlistDetails.numberOfPlaylistItems",
    options: {
      count
    }
  });
};
export const PlaylistItems = ({
  playlistId,
  searchInputRef
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const userOwnsPlaylist = useUserOwnsPlaylist(playlistId);
  const scrollRef = useRef(null);
  const {
    isPlaylistSearchManagerOpen
  } = usePlaylistSearchManagerOpenState();
  const Heading = isMobile ? H5 : H4;
  return /*#__PURE__*/_jsx(PlaylistItemManager, {
    playlistId: playlistId,
    render: playlistItems => /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("div", {
        ref: scrollRef,
        children: /*#__PURE__*/_jsx(Heading, {
          className: "m-bottom-1",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.savedItems.playlistDetails.playlistItems"
          })
        })
      }), /*#__PURE__*/_jsxs(PlaylistItemSearchProvider, {
        playlistId: playlistId,
        children: [userOwnsPlaylist && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs(UIFlex, {
            align: "center",
            justify: "between",
            children: [/*#__PURE__*/_jsxs(DividedList, {
              className: "m-bottom-2",
              children: [/*#__PURE__*/_jsx(PlaylistItemCount, {}), !isPlaylistSearchManagerOpen && /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.savedItems.playlistDetails.dragToReorder"
              }), !isMobile && !isPlaylistSearchManagerOpen && /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.savedItems.playlistDetails.deselectToRemove"
              })]
            }), isMobile && /*#__PURE__*/_jsx(PlaylistMobileFilterButton, {})]
          }), /*#__PURE__*/_jsxs(UIGrid, {
            children: [/*#__PURE__*/_jsx(UIGridItem, {
              size: {
                xs: 12,
                md: 6
              },
              children: /*#__PURE__*/_jsx(PlaylistItemSearch, {
                inputRef: searchInputRef
              })
            }), /*#__PURE__*/_jsx(UIGridItem, {
              size: {
                xs: 12,
                md: 6
              },
              children: /*#__PURE__*/_jsx(PlaylistItemSearchManager, {
                searchView: isMobile ? /*#__PURE__*/_jsx(Footer, {
                  children: /*#__PURE__*/_jsxs(UIGrid, {
                    children: [/*#__PURE__*/_jsx(UIGridItem, {
                      size: 6,
                      children: /*#__PURE__*/_jsx(CancelPlaylistSearchButton, {})
                    }), /*#__PURE__*/_jsx(UIGridItem, {
                      size: 6,
                      children: /*#__PURE__*/_jsx(SavePlaylistSearchButton, {})
                    })]
                  })
                }) : /*#__PURE__*/_jsxs(UIFlex, {
                  justify: "end",
                  children: [/*#__PURE__*/_jsx(CancelPlaylistSearchButton, {}), /*#__PURE__*/_jsx(SavePlaylistSearchButton, {})]
                })
              })
            })]
          })]
        }), /*#__PURE__*/_jsx(PlaylistItemSearchManager, {
          listView: playlistItems.length > 0 ? /*#__PURE__*/_jsx(PlaylistDragProvider, {
            children: /*#__PURE__*/_jsx(PlaylistItemsList, {
              className: "m-top-5",
              "data-test-id": "playlist-items-list",
              ordered: true,
              children: playlistItems.map((playlistItem, i) => {
                let Item = null;
                switch (playlistItem.type) {
                  case 'LESSON':
                    Item = LessonPlaylistItem;
                    break;
                  case 'TRACK':
                    Item = TrackPlaylistItem;
                    break;
                  case 'SHORT_FORM_CONTENT':
                    Item = ShortPlaylistItem;
                    break;
                  default:
                    return null;
                }
                return /*#__PURE__*/_jsx(Item, {
                  playlistId: playlistId,
                  playlistItem: playlistItem,
                  position: i
                }, playlistItem.itemId);
              })
            })
          }) : null,
          searchView: /*#__PURE__*/_jsxs(_Fragment, {
            children: [!isMobile && /*#__PURE__*/_jsx(TracksTopFiltersContainer, {}), /*#__PURE__*/_jsx(PlaylistItemSearchResults, {
              onSearchPageChange: () => {
                if (scrollRef.current) {
                  scrollRef.current.scrollIntoView();
                }
              }
            })]
          })
        })]
      })]
    })
  });
};